import React, { useState, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { useFlagVariations } from "@flopflip/react-broadcast";
import styled from "@emotion/styled";
import _ from "lodash";
import {
  StMenuContainer,
  StInnerMenuContainer,
  StHorizontalStroke,
} from "@mediamath/design-system-eng";
import { useLocation } from "core/shared/hooks/useStableRouter";
import { buildSupportUrl } from "core/app/util/SupportDefs";
import useUrlEntities from "core/shared/hooks/useUrlEntities";
import appMessages from "core/app/intl/AppMessages";
import AppFeatureFlags from "core/app/util/AppFeatureFlags";
import { Colors, BrandingColors } from "design-system";
import { useSideNav } from "core/app/hooks/useSideNav";
import useLegacyPlatformView from "core/app/hooks/useLegacyPlatformView";
import { matchPath, isNavItemVisible } from "core/shared/util/sideNavUtils";
import StaticLinks from "core/app/presenter/sideNav/SideNavStaticLinks";
import MenuItem from "core/app/presenter/sideNav/SideNavMenuItem";
import SubMenuList from "core/app/presenter/sideNav/SideNavSubMenu";
import VersionNumber from "core/app/presenter/sideNav/SideNavVersionNumber";
import SideNavToggles from "core/app/presenter/sideNav/SideNavToggles";

const StAnimatedMenuContainer = styled(StMenuContainer, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})`
  overflow: hidden;
  white-space: nowrap;
  width: 200px;
  will-change: transform;
  transform: translateX(${({ collapsed }) => (collapsed ? "-200px" : "0px")});
  margin-left: ${({ collapsed }) => (collapsed ? "-200px" : "0px")};
  transition: transform 300ms ease-in-out, margin-left 300ms ease-in-out;
`;

const StFlexMenuContainer = styled(StInnerMenuContainer)`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const AnimatedNavWrapper = React.memo(({ children }) => {
  const { collapsed } = useSideNav();
  return (
    <StAnimatedMenuContainer
      collapsed={collapsed}
      backgroundColor={BrandingColors.DarkBackground}
    >
      {children}
    </StAnimatedMenuContainer>
  );
});

AnimatedNavWrapper.displayName = "AnimatedNavWrapper";
AnimatedNavWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const ID_URL = "https://idlookup.mediamath.com/";
const MMA_URL = "https://academy.mediamath.com";
const KB_URL = "https://support.infillion.com/s/";

const flagKeys = [
  AppFeatureFlags.ADMIN_MODULE_COMPASS,
  AppFeatureFlags.ADMIN_MODULE,
  AppFeatureFlags.AUDIENCE_MODULE_COMPASS,
  AppFeatureFlags.AUDIENCE_MODULE,
  AppFeatureFlags.NEW_BULKMANAGER_MODULE,
  AppFeatureFlags.CAMPAIGNS_MODULE_COMPASS,
  AppFeatureFlags.CAMPAIGNS_MODULE,
  AppFeatureFlags.CREATIVES_MODULE_COMPASS,
  AppFeatureFlags.CREATIVES_MODULE,
  AppFeatureFlags.DARK_MODE,
  AppFeatureFlags.ENTERPRISE_CONTROLS_MODULE,
  AppFeatureFlags.MEDIA_MODULE_COMPASS,
  AppFeatureFlags.PLACES_MODULE,
  AppFeatureFlags.PLANS_MODULE,
  AppFeatureFlags.REPORTS_MODULE,
  AppFeatureFlags.REPORTS,
  AppFeatureFlags.SIDE_NAV,
];

const buildFeatureTogglesObject = (flagValues) =>
  flagKeys.reduce(
    (obj, currVal, index) => ({ ...obj, [currVal]: flagValues[index] }),
    {}
  );

const SideNav = ({ onSelect = () => {}, data = [] }) => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const supportUrl = buildSupportUrl({
    ids: useUrlEntities(),
    pageUrl: window.location.href,
  });
  const { isLegacyPlatform, showLegacyPlatformView } = useLegacyPlatformView();

  const flagValues = useFlagVariations(flagKeys);
  const featureToggles = useMemo(() => buildFeatureTogglesObject(flagValues), [
    flagValues,
  ]);

  const { isAuthenticated, user } = useAuth0();
  const username = user?.nickname;
  const [expandedItem, setExpandedItem] = useState(null);

  const hasFeatureFlag = useCallback(
    (flag) => _.get(featureToggles, flag, null),
    [featureToggles]
  );

  const toggleSubMenu = useCallback(
    (key) => setExpandedItem((prev) => (prev === key ? null : key)),
    []
  );

  if (!featureToggles[AppFeatureFlags.SIDE_NAV]) return null;

  return (
    <AnimatedNavWrapper>
      <StFlexMenuContainer>
        {data.map((item) => {
          const {
            beta,
            flag,
            intlKey,
            isSmartLink,
            key,
            legacy,
            matches,
            poc,
            subMenu,
          } = item;

          if (
            !isNavItemVisible({
              isLegacyPlatform,
              legacy,
              hasFeatureFlag,
              flag,
            })
          )
            return null;

          const isActive = matchPath({ location, matches });
          const isNonDefaultSubActive = subMenu?.some(
            (subItem, idx) =>
              idx !== 0 && matchPath({ location, matches: subItem.matches })
          );

          return (
            <React.Fragment key={key}>
              <MenuItem
                active={isActive}
                beta={beta || poc || legacy}
                betaLabel={
                  (poc && formatMessage(appMessages.poc)) ||
                  (beta && formatMessage(appMessages.beta)) ||
                  (legacy && formatMessage(appMessages.legacy))
                }
                data={item}
                expanded={expandedItem === key}
                isSmartLink={isSmartLink}
                label={formatMessage(intlKey)}
                onSelect={onSelect}
                preventNav={Boolean(isNonDefaultSubActive)}
                toggleClick={toggleSubMenu}
              />
              {subMenu && (
                <SubMenuList
                  expanded={expandedItem === key}
                  hasFeatureFlag={hasFeatureFlag}
                  isLegacyPlatform={isLegacyPlatform}
                  location={location}
                  onSelect={onSelect}
                  subMenu={subMenu}
                  toggleSubMenu={toggleSubMenu}
                />
              )}
            </React.Fragment>
          );
        })}
        <StHorizontalStroke color={Colors.White} />
        <StaticLinks title={formatMessage(appMessages.idLookup)} url={ID_URL} />
        <StaticLinks title={formatMessage(appMessages.kb)} url={KB_URL} />
        <StaticLinks
          title={formatMessage(appMessages.mmAcademy)}
          url={MMA_URL}
        />
        <StaticLinks
          title={formatMessage(appMessages.support)}
          url={supportUrl}
        />
        <StHorizontalStroke color={Colors.White} />
        {isAuthenticated && (
          <MenuItem
            data={{ key: "logout", link: "/logout", flag: null }}
            isSmartLink={false}
            label={formatMessage(appMessages.logout)}
            noHover
            user={username}
            toggleClick={toggleSubMenu}
          />
        )}
        <SideNavToggles
          showLegacyPlatformView={showLegacyPlatformView}
          isDarkMode={featureToggles[AppFeatureFlags.DARK_MODE]}
        />
        <VersionNumber />
      </StFlexMenuContainer>
    </AnimatedNavWrapper>
  );
};

SideNav.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({ subMenu: PropTypes.arrayOf(PropTypes.shape({})) })
  ),
  onSelect: PropTypes.func,
};

export default SideNav;
